/* _customled-form-validation.js — Custom LED owned  [M1.21]
 *
 * Clear, inline, per-field validation messages for the flexi-contact-form forms
 * (Contact + Dealer Application). The vendor form relies on the browser's native
 * HTML5 validation bubble, which is terse, shows only the FIRST invalid field, and
 * vanishes on blur — so a shopper who leaves a required field empty gets a vague,
 * easy-to-miss tooltip (publish-readiness UX finding).
 *
 * This enhancement (Constraint Validation API):
 *   - suppresses the native bubble (capture-phase `invalid`, which doesn't bubble)
 *   - renders a persistent inline message UNDER each invalid field, worded from the
 *     field's own label ("Business Email is required" / "Enter a valid email…")
 *   - marks the field aria-invalid + wires aria-describedby for screen readers
 *   - shows ALL errors at once, focuses+scrolls to the first
 *   - clears each message the moment that field becomes valid
 * Native submission still posts normally once every field is valid; server-side
 * errors (email format / captcha) keep using the vendor's existing summary block.
 *
 * Owned, zero vendor edits. Loaded page-gated by _customled-stylesheets.liquid;
 * self-no-ops if no flexi-contact-form is present. Styling: _customled-form-validation.css.
 */
(function () {
  "use strict";
  var FORM_SELECTOR = "form.flexi-contact-form";

  function labelText(field) {
    var lbl = field.id ? field.form.querySelector('label[for="' + field.id + '"]') : null;
    var t = lbl ? lbl.textContent : (field.getAttribute("aria-label") || field.name || "This field");
    return t.replace(/\*/g, "").replace(/\s+/g, " ").trim();
  }

  function messageFor(field) {
    var name = labelText(field);
    var v = field.validity;
    if (v.valueMissing) return name + " is required.";
    if (v.typeMismatch) {
      if (field.type === "email") return "Enter a valid email address, like name@example.com.";
      if (field.type === "tel") return "Enter a valid phone number.";
      return "Enter a valid " + name + ".";
    }
    if (v.tooShort || v.tooLong || v.patternMismatch || v.badInput ||
        v.rangeUnderflow || v.rangeOverflow || v.stepMismatch) {
      return "Enter a valid " + name + ".";
    }
    return field.validationMessage || (name + " is invalid.");
  }

  function errIdFor(field) {
    return (field.id || field.name || "cl-field") + "-cl-error";
  }

  function showError(field) {
    var errId = errIdFor(field);
    var el = document.getElementById(errId);
    if (!el) {
      el = document.createElement("small");
      el.className = "customled-field-error";
      el.id = errId;
      var container = field.closest(".flexi-block") || field.closest(".field") || field.parentNode;
      container.appendChild(el);
    }
    el.textContent = messageFor(field);
    field.setAttribute("aria-invalid", "true");
    field.classList.add("customled-field-invalid");
    var desc = field.getAttribute("aria-describedby") || "";
    if (desc.split(/\s+/).indexOf(errId) === -1) {
      field.setAttribute("aria-describedby", (desc + " " + errId).trim());
    }
  }

  function clearError(field) {
    var el = document.getElementById(errIdFor(field));
    if (el) el.parentNode.removeChild(el);
    field.removeAttribute("aria-invalid");
    field.classList.remove("customled-field-invalid");
  }

  function enhance(form) {
    if (form.__customledValidationBound) return;
    form.__customledValidationBound = true;

    // 'invalid' fires (capture phase) on each bad control when submission is attempted.
    form.addEventListener("invalid", function (e) {
      e.preventDefault(); // kill the native bubble; we render our own
      showError(e.target);
      if (!form.__clFirstInvalid) {
        form.__clFirstInvalid = e.target;
        requestAnimationFrame(function () {
          var first = form.__clFirstInvalid;
          form.__clFirstInvalid = null;
          if (first) {
            try { first.focus({ preventScroll: true }); } catch (_) { first.focus(); }
            first.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        });
      }
    }, true);

    form.querySelectorAll("input, textarea, select").forEach(function (f) {
      var revalidate = function () { if (f.validity.valid) clearError(f); };
      f.addEventListener("input", revalidate);
      f.addEventListener("change", revalidate);
    });
  }

  function init() {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    if (!forms.length) return;
    forms.forEach(enhance);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
